import { Effect, Option } from 'effect';
import * as AtomRegistry from 'effect/unstable/reactivity/AtomRegistry';

import {
  makeAddToCart,
  retireAcceptedPendingLinesForVariant,
  type AddToCartDeps,
} from './addToCart';
import { makeCartMutationPermit } from './cartMutationQueue';
import { CART_REFRESH_TIMEOUT } from './cartTransport';

/** One cart session owns one write permit and one optimistic add command. */
export const makeCartMutations = <P, D extends { readonly token: string }, E, R, RT>(
  deps: Omit<AddToCartDeps<P, D, E, R, RT>, 'withPermit'> &
    Partial<Pick<AddToCartDeps<P, D, E, R, RT>, 'withPermit'>>
) => {
  const withPermit = deps.withPermit ?? makeCartMutationPermit();
  const add = makeAddToCart({ ...deps, withPermit });

  const refresh = Effect.gen(function* () {
    const registry = yield* AtomRegistry.AtomRegistry;
    registry.refresh(deps.cartAtom);
    yield* AtomRegistry.getResult(registry, deps.cartAtom, { suspendOnWaiting: true }).pipe(
      Effect.timeoutOption(CART_REFRESH_TIMEOUT),
      Effect.option
    );
  });

  const serialize =
    <Arg, A, E1, R1>(mutation: (arg: Arg) => Effect.Effect<A, E1, R1>) =>
    (arg: Arg) =>
      withPermit(mutation(arg));

  const performWrite =
    <Arg, A, E1, R1>(
      mutation: (arg: Arg) => Effect.Effect<A, E1, R1>,
      changedVariant: (arg: Arg, result: A) => Option.Option<string>
    ) =>
    (arg: Arg) =>
      withPermit(
        mutation(arg).pipe(
          Effect.tap((result) =>
            Option.match(changedVariant(arg, result), {
              onNone: () => Effect.void,
              onSome: (variantCode) =>
                retireAcceptedPendingLinesForVariant(deps.state.pendingCartLinesAtom, variantCode),
            })
          ),
          Effect.onExit(() => refresh)
        )
      );

  /** Serialize a Cart write without a line retirement. */
  const write = <Arg, A, E1, R1>(mutation: (arg: Arg) => Effect.Effect<A, E1, R1>) =>
    performWrite(mutation, () => Option.none<string>());

  /** A successful line write supersedes accepted pending adds for its variant. */
  const writeLine = <Arg, A, E1, R1>(
    mutation: (arg: Arg) => Effect.Effect<A, E1, R1>,
    changedVariant: (arg: Arg, result: A) => Option.Option<string>
  ) => performWrite(mutation, changedVariant);

  return { add, serialize, write, writeLine };
};
